import React, { useMemo, useState } from 'react';
import { Sidebar } from './Sidebar';
import { useBankSms } from './BankSmsContext';
import { useOrders } from './OrdersContext';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertTriangle, XCircle, Search, Smartphone, Building2, ShieldCheck, Inbox, Menu as MenuIcon } from 'lucide-react';
export function BankPaymentsPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'matched' | 'review' | 'unmatched'>('all');
  const {
    messages,
    simulateIncoming,
    markVerified,
    dismiss
  } = useBankSms();
  const {
    orders,
    setPaymentStatus
  } = useOrders();
  const pendingOrders = useMemo(() => orders.filter(o => o.paymentMethod === 'bank-transfer' && o.paymentStatus === 'pending' && o.status !== 'cancelled'), [orders]);
  // Match logic
  const enrichedMessages = useMemo(() => {
    return messages.map(sms => {
      if (sms.verifiedOrderId) {
        const order = orders.find(o => o.id === sms.verifiedOrderId);
        return {
          sms,
          state: 'verified' as const,
          order
        };
      }
      if (sms.dismissed) {
        return {
          sms,
          state: 'dismissed' as const
        };
      }
      // Find exact match
      const exactMatch = pendingOrders.find(o => Math.abs(o.total - sms.amount) < 0.01 && o.bankTransferRef?.toUpperCase() === sms.reference.toUpperCase());
      if (exactMatch) {
        return {
          sms,
          state: 'matched' as const,
          order: exactMatch
        };
      }
      // Find partial match (amount only)
      const partialMatches = pendingOrders.filter(o => Math.abs(o.total - sms.amount) < 0.01);
      if (partialMatches.length > 0) {
        return {
          sms,
          state: 'review' as const,
          possibleOrders: partialMatches
        };
      }
      return {
        sms,
        state: 'unmatched' as const
      };
    }).sort((a, b) => b.sms.receivedAt - a.sms.receivedAt);
  }, [messages, pendingOrders, orders]);
  const filtered = enrichedMessages.filter(m => {
    if (filter !== 'all' && m.state !== filter && !(filter === 'matched' && m.state === 'verified')) return false;
    if (search) {
      const q = search.toLowerCase();
      return m.sms.raw.toLowerCase().includes(q) || m.sms.amount.toString().includes(q) || m.sms.reference.toLowerCase().includes(q);
    }
    return true;
  });
  const handleVerify = (smsId: string, orderId: string) => {
    setPaymentStatus(orderId, 'paid');
    markVerified(smsId, orderId);
  };
  return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-306">
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30" data-id="element-307">
        <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-308">
          <MenuIcon size={24} data-id="element-309" />
        </button>
        <span className="font-semibold" data-id="element-310">Bank Payments</span>
        <div className="w-6" data-id="element-311" />
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-312" />

      <main className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-313">
        <div className="px-4 md:px-6 pt-5 md:pt-6 pb-4 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-314">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4" data-id="element-315">
            <div data-id="element-316">
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3" data-id="element-317">
                Bank Payments
              </h1>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-318">
                Incoming bank SMS auto-matched to orders
              </p>
            </div>
            <button onClick={simulateIncoming} className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-bold hover:bg-indigo-100 transition-colors flex items-center gap-2" data-id="element-319">
              <Smartphone size={16} data-id="element-320" />
              Simulate incoming SMS
            </button>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center gap-3" data-id="element-321">
            <div className="relative flex-1 max-w-md" data-id="element-322">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-323" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SMS..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition" data-id="element-324" />
            </div>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto" data-id="element-325">
              {(['all', 'matched', 'review', 'unmatched'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors capitalize ${filter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-326">
                  {f}
                </button>)}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-id="element-327">
          <AnimatePresence data-id="element-328">
            {filtered.map(item => <motion.div key={item.sms.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-id="element-329">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-330">
                  <div className="flex-1" data-id="element-331">
                    <div className="flex items-center gap-2 mb-2" data-id="element-332">
                      <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center" data-id="element-333">
                        <Building2 size={16} data-id="element-334" />
                      </div>
                      <div data-id="element-335">
                        <div className="font-bold text-gray-900 text-sm" data-id="element-336">
                          ETB {item.sms.amount.toLocaleString()}
                        </div>
                        <div className="text-[11px] text-gray-500" data-id="element-337">
                          {new Date(item.sms.receivedAt).toLocaleString()} ·
                          Ref: {item.sms.reference}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg border border-gray-100 font-mono text-xs break-all" data-id="element-338">
                      {item.sms.raw}
                    </div>
                  </div>

                  <div className="w-full md:w-72 flex-shrink-0" data-id="element-339">
                    {item.state === 'matched' && item.order && <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-id="element-340">
                        <div className="flex items-center gap-1.5 text-emerald-700 font-bold text-xs mb-2 uppercase tracking-wide" data-id="element-341">
                          <CheckCircle2 size={14} data-id="element-342" /> Exact Match
                        </div>
                        <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-343">
                          Order {item.order.id}
                        </div>
                        <div className="text-xs text-gray-600 mb-3" data-id="element-344">
                          {item.order.customerName}
                        </div>
                        <button onClick={() => handleVerify(item.sms.id, item.order!.id)} className="w-full py-2 bg-emerald-500 text-white rounded-md text-xs font-bold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1.5" data-id="element-345">
                          <ShieldCheck size={14} data-id="element-346" /> Verify & Settle
                        </button>
                      </div>}

                    {item.state === 'review' && item.possibleOrders && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-id="element-347">
                        <div className="flex items-center gap-1.5 text-amber-700 font-bold text-xs mb-2 uppercase tracking-wide" data-id="element-348">
                          <AlertTriangle size={14} data-id="element-349" /> Needs Review
                        </div>
                        <div className="text-xs text-amber-800 mb-2" data-id="element-350">
                          Amount matches {item.possibleOrders.length} pending
                          order(s), but reference differs.
                        </div>
                        <div className="space-y-2" data-id="element-351">
                          {item.possibleOrders.map(o => <div key={o.id} className="flex items-center justify-between bg-white p-2 rounded border border-amber-100" data-id="element-352">
                              <div data-id="element-353">
                                <div className="text-xs font-bold text-gray-900" data-id="element-354">
                                  {o.id}
                                </div>
                                <div className="text-[10px] text-gray-500" data-id="element-355">
                                  Ref: {o.bankTransferRef || 'None'}
                                </div>
                              </div>
                              <button onClick={() => handleVerify(item.sms.id, o.id)} className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-[10px] font-bold hover:bg-amber-200" data-id="element-356">
                                Match
                              </button>
                            </div>)}
                        </div>
                      </div>}

                    {item.state === 'unmatched' && <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-357">
                        <div className="flex items-center gap-1.5 text-gray-600 font-bold text-xs mb-2 uppercase tracking-wide" data-id="element-358">
                          <Inbox size={14} data-id="element-359" /> Unmatched
                        </div>
                        <div className="text-xs text-gray-500 mb-3" data-id="element-360">
                          No pending orders match this amount. Likely an
                          off-platform payment.
                        </div>
                        <button onClick={() => dismiss(item.sms.id)} className="w-full py-1.5 bg-white border border-gray-200 text-gray-600 rounded-md text-xs font-bold hover:bg-gray-100 transition-colors" data-id="element-361">
                          Dismiss
                        </button>
                      </div>}

                    {item.state === 'verified' && item.order && <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 opacity-75" data-id="element-362">
                        <div className="flex items-center gap-1.5 text-emerald-600 font-bold text-xs mb-1 uppercase tracking-wide" data-id="element-363">
                          <ShieldCheck size={14} data-id="element-364" /> Verified
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-365">
                          Matched to Order {item.order.id}
                        </div>
                      </div>}

                    {item.state === 'dismissed' && <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 opacity-75" data-id="element-366">
                        <div className="flex items-center gap-1.5 text-gray-500 font-bold text-xs mb-1 uppercase tracking-wide" data-id="element-367">
                          <XCircle size={14} data-id="element-368" /> Dismissed
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-369">
                          Archived manually
                        </div>
                      </div>}
                  </div>
                </div>
              </motion.div>)}
            {filtered.length === 0 && <div className="text-center py-12" data-id="element-370">
                <div className="w-12 h-12 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-371">
                  <Inbox size={24} data-id="element-372" />
                </div>
                <div className="text-sm font-bold text-gray-900" data-id="element-373">
                  No messages found
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-374">
                  Try adjusting your filters or simulate an incoming SMS.
                </div>
              </div>}
          </AnimatePresence>
        </div>
      </main>
    </div>;
}